import { useState } from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AddBoxIcon from '@mui/icons-material/AddBox'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import { styled, useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useMemo } from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

import IconButtonOutlined from '../root/components/IconButtonOutlined'
import BreadCrumbs from '../root/components/BreadCrumbs'
import { HEADER_HEIGHT } from '../root/components/Header'
import SubHeader from '../root/components/SubHeader'
import { useLazyGetQRCodeQuery, useLazyGetPdfProgramQuery } from '../../services/events'
import { resetEventDetails } from '../../store.slice'
import { useAppDispatch } from '../../store'

const Background = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.secondaryBackground,
  height: `calc(100% - ${HEADER_HEIGHT})`,
  width: '100%',
}))

export default function Admin() {
  const { t } = useTranslation('admin')
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { eventId, optionId, participantId } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const [showStatistics, setShowStatistics] = useState(false)

  const isDetails = location.pathname.includes('/details')
  const isEvents = location.pathname.includes('/events')
  const isSubEvent = location.pathname.includes('/subevents')
  const isParticipants = location.pathname.includes('/participants')
  const isHotels = location.pathname.includes('/hotels')
  const isMeals = location.pathname.includes('/meals')
  const isOptions = location.pathname.includes('/options')
  const isSponsorship = location.pathname.includes('/sponsorship')
  const isContributors = location.pathname.includes('/contributors')
  const isAbstracts = location.pathname.includes('/abstracts')

  const [getQRCode] = useLazyGetQRCodeQuery()
  const [getPdfProgram] = useLazyGetPdfProgramQuery()

  const links = useMemo(() => {
    const linksToCompute = [{ name: t('Administration'), pathname: '/admin' }]
    if (isDetails || isEvents) {
      linksToCompute.push({
        name: isDetails ? t('Détails') : '',
        pathname: `/admin/details/${eventId}/subevents`,
      })
    }
    if (isHotels) {
      linksToCompute.push({
        name: t('Hôtels'),
        pathname: `/admin/details/${eventId}/hotels`,
      })
    }
    if (isMeals) {
      linksToCompute.push({
        name: t('Repas'),
        pathname: `/admin/details/${eventId}/meals`,
      })
    }
    if (isContributors) {
      linksToCompute.push({
        name: t('Contributeurs'),
        pathname: `/admin/details/${eventId}/contributors`,
      })
    }
    if (isOptions) {
      linksToCompute.push({
        name: t('Options'),
        pathname: `/admin/details/${eventId}/options`,
      })
    }
    if (isSubEvent) {
      linksToCompute.push({
        name: t('Interventions'),
        pathname: `/admin/details/${eventId}/subevents`,
      })
    }
    if (isAbstracts) {
      linksToCompute.push({
        name: t('Résumés'),
        pathname: `/admin/details/${eventId}/abstracts`,
      })
    }
    if (isSponsorship) {
      linksToCompute.push({
        name: t('Prise en charge'),
        pathname: `/admin/details/${eventId}/sponsorship`,
      })
    }
    if (isParticipants) {
      linksToCompute.push({
        name: t('Participants'),
        pathname: `/admin/details/${eventId}/participants`,
      })
    }
    if (participantId ?? optionId) {
      linksToCompute.push({
        name: '',
        pathname: '',
      })
    }
    return linksToCompute
  }, [
    t,
    isDetails,
    isEvents,
    isHotels,
    isMeals,
    isContributors,
    isOptions,
    isSubEvent,
    isAbstracts,
    isSponsorship,
    isParticipants,
    participantId,
    optionId,
    eventId,
  ])

  return (
    <Background>
      <SubHeader>
        <BreadCrumbs
          hasSeparatorEnd={(participantId ?? optionId) ? false : !isEvents}
          links={links}
          sx={{ flex: { xs: '1 0 0', sm: '1 0 auto', lg: '1 0 0' } }}
        />
        <Typography variant="h3" color="text.primary">
          {t('Tableau de bord')}
        </Typography>
        <Stack
          direction="row"
          justifyContent="end"
          flex={{ xs: '1 0 0', lg: '1 0 auto' }}
          gap="0.5rem"
        >
          {!eventId ? (
            <Button
              startIcon={<AddBoxIcon />}
              variant="contained"
              onClick={e => {
                e.preventDefault()
                dispatch(resetEventDetails('new'))
                navigate('/admin/events/new/event')
              }}
              href="/admin/events/new/event"
              sx={{ flex: '0 1 max-content' }}
            >
              {isMobile ? t('Événement') : t('Nouvel événement')}
            </Button>
          ) : (
            <>
              <IconButtonOutlined
                size="small"
                onClick={() => {
                  setShowStatistics(!showStatistics)
                }}
                title={showStatistics ? t('Masquer Statistiques') : t('Afficher Statistiques')}
                sx={{ display: isEvents ? 'none' : undefined }}
              >
                <QueryStatsIcon fontSize="small" />
              </IconButtonOutlined>
              <IconButtonOutlined
                size="small"
                onClick={() => {
                  void getQRCode(Number(eventId))
                }}
                title={t('Générer le QR code')}
                sx={{ display: isEvents ? 'none' : undefined }}
              >
                <QrCode2Icon fontSize="small" />
              </IconButtonOutlined>
              <IconButtonOutlined
                size="small"
                onClick={() => {
                  void getPdfProgram(Number(eventId))
                }}
                title={t('Générer le PDF du programme')}
                sx={{ display: isEvents ? 'none' : undefined }}
              >
                <PictureAsPdfIcon fontSize="small" />
              </IconButtonOutlined>
              <IconButtonOutlined
                size="small"
                onClick={() => {
                  navigate(`/events/${eventId}`)
                }}
                title={t('Voir le programme')}
                sx={{ display: eventId === 'new' ? 'none' : undefined }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButtonOutlined>
              <IconButtonOutlined
                size="small"
                onClick={() => {
                  navigate(`/admin/events/${eventId}/event`)
                }}
                title={t('Modifier l’événement')}
                sx={{ display: isEvents ? 'none' : undefined }}
              >
                <EditIcon fontSize="small" />
              </IconButtonOutlined>
            </>
          )}
        </Stack>
      </SubHeader>
      <Stack
        sx={theme => ({
          background: theme.palette.background.default,
          width: '100%',
          height: '100%',
          padding: '0.5rem',
          overflowY: 'auto',
        })}
        gap="1rem"
      >
        <Outlet
          context={{
            showStatistics,
          }}
        />
      </Stack>
    </Background>
  )
}
